import { useEffect, useRef, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { WS_URL } from '../../../shared/utils/constants';

/**
 * ContainerExec Component
 * Interactive terminal for executing commands inside a container
 */
export default function ContainerExec({ container, onClose }) {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const socketRef = useRef(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) {
      console.log('[Docker Exec] Already initialized, skipping');
      return;
    }
    hasInitialized.current = true;
    console.log('[Docker Exec] Initializing terminal');

    // Initialize xterm.js
    const term = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1a1a1a',
        foreground: '#f0f0f0',
        cursor: '#f0f0f0',
        selection: 'rgba(255, 255, 255, 0.3)',
        black: '#000000',
        red: '#e06c75',
        green: '#98c379',
        yellow: '#d19a66',
        blue: '#61afef',
        magenta: '#c678dd',
        cyan: '#56b6c2',
        white: '#abb2bf',
        brightBlack: '#5c6370',
        brightRed: '#e06c75',
        brightGreen: '#98c379',
        brightYellow: '#d19a66',
        brightBlue: '#61afef',
        brightMagenta: '#c678dd',
        brightCyan: '#56b6c2',
        brightWhite: '#ffffff'
      },
      allowProposedApi: true
    });

    // Addons
    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);

    // Open terminal
    term.open(terminalRef.current);
    fitAddon.fit();

    // Store refs
    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Connect to WebSocket
    const socket = io(WS_URL, {
      auth: {
        token: localStorage.getItem('authToken') || sessionStorage.getItem('telegram_auth')
      }
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[Docker Exec] WebSocket connected');
      setIsConnecting(false);

      // Create Docker exec session
      console.log('[Docker Exec] Emitting docker:exec:create', {
        containerId: container.id,
        cols: term.cols,
        rows: term.rows
      });
      socket.emit('docker:exec:create', {
        containerId: container.id,
        cols: term.cols,
        rows: term.rows
      });
    });

    socket.on('docker:exec:data', (data) => {
      console.log('[Docker Exec] Received data:', data.data.substring(0, 100));
      term.write(data.data);
    });

    socket.on('docker:exec:error', (data) => {
      console.error('[Docker Exec] Error:', data.message);
      term.write(`\r\n\x1b[31mError: ${data.message}\x1b[0m\r\n`);
      setError(data.message);
      setIsConnecting(false);
    });

    socket.on('docker:exec:exit', () => {
      console.log('[Docker Exec] Session ended by server');
      term.write('\r\n\n\x1b[33m[Container exec session ended]\x1b[0m\r\n');
      // Don't auto-close, let user see the message and close manually
      setIsConnecting(false);
    });

    socket.on('connect_error', (err) => {
      console.error('[Docker Exec] WebSocket connection error:', err);
      setError('Connection failed: ' + err.message);
      setIsConnecting(false);
    });

    socket.on('disconnect', (reason) => {
      console.log('[Docker Exec] WebSocket disconnected:', reason);
      if (reason === 'io server disconnect' || reason === 'io client disconnect') {
        // Normal disconnect, don't try to reconnect
        console.log('[Docker Exec] Normal disconnect, not reconnecting');
      }
    });

    // Handle user input
    term.onData((input) => {
      if (socket.connected) {
        socket.emit('docker:exec:input', { input });
      }
    });

    // Handle terminal resize
    const handleResize = () => {
      fitAddon.fit();
      if (socket.connected) {
        socket.emit('docker:exec:resize', {
          cols: term.cols,
          rows: term.rows
        });
      }
    };

    window.addEventListener('resize', handleResize);

    // Telegram BackButton
    if (window.Telegram?.WebApp?.BackButton) {
      window.Telegram.WebApp.BackButton.onClick(onClose);
      window.Telegram.WebApp.BackButton.show();
    }

    // Cleanup
    return () => {
      console.log('[Docker Exec] Cleanup triggered');
      window.removeEventListener('resize', handleResize);

      if (window.Telegram?.WebApp?.BackButton) {
        window.Telegram.WebApp.BackButton.offClick(onClose);
        window.Telegram.WebApp.BackButton.hide();
      }

      if (socket && socket.connected) {
        console.log('[Docker Exec] Closing socket');
        socket.emit('docker:exec:close');
        socket.disconnect();
      }

      if (term) {
        term.dispose();
      }

      hasInitialized.current = false;
    };
  }, []); // Empty deps - only run once

  const getContainerName = () => {
    if (Array.isArray(container.names) && container.names.length > 0) {
      return container.names[0].replace(/^\//, '');
    }
    return 'unnamed';
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-white font-mono text-sm">
            {getContainerName()} - exec
          </span>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
        >
          <FontAwesomeIcon icon={faXmark} className="text-white" />
        </button>
      </div>

      {/* Loading / Error overlay */}
      {(isConnecting || error) && (
        <div className="absolute inset-0 bg-black bg-opacity-80 flex items-center justify-center z-10">
          {isConnecting ? (
            <div className="text-white flex flex-col items-center gap-4">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Connecting to container...</p>
            </div>
          ) : error ? (
            <div className="text-red-500 flex flex-col items-center gap-4">
              <p className="text-xl">⚠️ Error</p>
              <p>{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Close
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Terminal */}
      <div ref={terminalRef} className="flex-1 p-2" />
    </div>
  );
}
